import { api } from '../lib/api.js';

export type FinanceRates = {
  departments: { id: string; name: string; hourlyRate: number }[];
  installationHourlyRate: number;
  /** ₪ לק"מ. 0 = הק"מ מדווחים ואינם מחויבים. */
  vehicleKmRate: number;
  /** שערי המרה — הערך בשקלים שעליו נעולה הצעה במטבע חוץ ברגע השליחה. */
  fx: { eur: number; usd: number };
  service: { callOut: number; hourlyPrice: number; partsMarkupPct: number };
  targetMargin: { project: number; shortJob: number; service: number };
  /** לילה בהתקנה: אוכל ולינה לעובד, ומלון לכל N עובדים. */
  night: { meal: number; allowance: number; hotel: number; hotelPerWorkers: number; hoursPerDay: number };
};

export type FinanceProjectInput = {
  id: string; number: string; name: string | null; managerName: string | null;
  targetDate: string | null;
  purchasePlanned: number | null; purchaseActual: number | null;
  salePrice: number | null; installPlannedHours: number | null; installActualHours: number;
};

export type FinanceCostCat = {
  plannedHours: number; actualHours: number; planned: number; actual: number;
  /** מאיפה הגיע התקציב. 'NONE' = איש לא הזין, ואז planned אינו אפס אלא לא ידוע. */
  plannedSource: 'ESTIMATE' | 'ROLLUP' | 'NONE';
};
export type ClosingEstimate = {
  ils: number;
  productionMinutes: number; productionIls: number;
  engineeringHours: number; engineeringIls: number;
  purchaseIls: number; subcontractIls: number;
  closedAt: string; closedByName: string | null;
};

export type FinanceRow = {
  id: string; number: string; name: string | null; managerName: string | null; targetDate: string | null;
  customerName: string | null; engineerName: string | null; committedDeliveryDate: string | null;
  purchase: {
    planned: number;
    /** false = לא הוזן תקציב רכש. ה₪0 שלידו אינו מדידה. */
    plannedKnown: boolean;
    actual: number; missingCount: number;
    /** נקנה לפרויקט וטרם נופק — כסף שכבר יצא, שאינו עדיין עלות של הפרויקט. */
    awaitingIssueIls: number; awaitingIssueLines: number; awaitingIssueArrived: number;
    /** שורות שכבר נקנו ואין להן מחיר — החוסר במספר שלצידן. */
    awaitingIssueUnpriced: number;
    /** רכש בפועל מפוצל: מכני / חשמל / בקרה / כללי. */
    byDiscipline: Record<'MECHANICAL' | 'ELECTRICAL' | 'CONTROL' | 'GENERAL', number>;
    /**
     * התקציב, באותו פירוט — כדי שיהיה מול מה למדוד.
     *
     * Filled by quote conversion since the split was built, selected by the
     * board's query, and read by nothing until now: the actual per discipline
     * had no plan beside it. A project with no split reports its whole budget
     * under ״כללי״ rather than as four zeros.
     */
    plannedByDiscipline: Record<'MECHANICAL' | 'ELECTRICAL' | 'CONTROL' | 'GENERAL', number>;
  };
  /**
   * חומר גלם — פלדה שנחתכה בלייזר. אין מאחוריה דרישת רכש, ולכן
   * עד 27.8.2026 היא לא הופיעה בשום ספירת עלויות.
   */
  rawMaterial: { actual: number; kg: number; sheets: number; metres: number; missingCount: number };
  /** רכב — ק"מ, דלק, כבישים והוצאות שחויבו לפרויקט. */
  vehicle: {
    km: number; trips: number; paidIls: number; distanceIls: number; actual: number;
    /** הוצאות רכב שנזקפו לפרויקט ואין להן סכום — הסכום שלמעלה חסר אותן. */
    unpricedEvents: number;
  };
  /** זמן לייזר ומסור שהוקצה לפרויקט. */
  /**
   * זמן מכונה — ומה שעוד לא נחתך, בנפרד ממה שלא תומחר.
   *
   * `unpriced` = a cut that HAPPENED and nobody costed; the ₪ figure is short
   * by it. `pending` = a cut that has not happened yet, which is not a gap in
   * the money at all. Merging the two made a project with a full saw queue read
   * as one hiding a quarter of its cost — found on 260283, fifteen of
   * sixty-five bars.
   */
  machine: { minutes: number; cost: number; unpriced: number; pending: number };
  /** עבודות תומכות ששויכו לפרויקט. */
  support: { hours: number; cost: number; unpricedHours: number };
  engineering: FinanceCostCat; production: FinanceCostCat; installation: FinanceCostCat;
  /**
   * ליווי מנהל פרויקט + תמיכה הנדסית — שעות שההצעה גובה עליהן, ושהתוכנית לא
   * הכילה עד 30.8.2026.
   */
  overhead: { pmHours: number; engSupportHours: number; planned: number };
  /** משלוח — מתוכנן מול בפועל (הובלות שנופקו מלוח המשלוח). */
  shipping: { planned: number; actual: number; missingCount: number };
  /** קבלני משנה — מתוכנן (מחיר מוסכם) מול בפועל (דרישות קבלן משנה), לכל דיסציפלינה. */
  subcontract: SubcontractCost;
  /** הרזרבות, ובאיזה אחוז — פיתוח ואחריות, על העלות הישירה. */
  reserves: { contingencyPct: number; contingency: number; warrantyPct: number; warranty: number };
  plannedCost: number; actualCost: number; variance: number; variancePct: number | null;
  /**
   * החלק מהביצוע שאין לו שורה בתקציב — חומר גלם, זמן מכונה, עבודות תומכות, רכב.
   *
   * "חרגנו ב-50 אלף, מתוכם 58 אלף מעולם לא תוקצבו" זה משפט אחר מ"חרגנו ב-50
   * אלף", ורק אחד מהם נכון.
   */
  unplannedActual: number;
  /**
   * „הערכת סגירה" — מה שנוסף בטופס הסגירה על אחריות. כבר בתוך `actualCost`;
   * המסך מסמן אותה כהערכה ולא כבפועל. null = לא נסגר על אחריות.
   */
  closingEstimate: ClosingEstimate | null;
  /** התאריכים מול היעדים שלהם — הורדה לייצור, PDR, CDR, FAT, משלוח. */
  dates: {
    plannedRelease: string | null; actualRelease: string | null;
    plannedPdr: string | null; actualPdr: string | null;
    plannedCdr: string | null; actualCdr: string | null;
    /** SRR / PRR — null כשהסקר אינו נכלל בפרויקט. */
    plannedSrr: string | null; actualSrr: string | null;
    plannedPrr: string | null; actualPrr: string | null;
    fatAt: string | null; shippedAt: string | null; completedAt: string | null;
  };
  salePrice: number | null; profit: number | null; profitPct: number | null;
  /**
   * ── נטו מרווחיות ─────────────────────────────────────────────────────────
   *
   * Eli, 2026-08-31: "אם הפרויקט נמכר ב־100 שקל, העלות תהיה מתחת ל־60 שקל" —
   * profit over the PRICE, from cost actually counted, never from an estimate.
   * "נטו מרווחיות בכל המסכים זה מה שהכי מעניין."
   *
   * threshold  which of 40/30/20/10/0 the margin has fallen to; null while
   *            healthy. The same value the alert fires on.
   * tone       one colour scale for the whole system, so red means the same
   *            thing on the TV as on the project file.
   * planned    what the accepted quote planned for, and the distance from it.
   *            Null where the project did not come from a costed quote — never
   *            invented from the budget, which answers a different question.
   */
  /**
   * שעות שנספרות בתעריף אפס — לא מתומחרות כלל.
   *
   * A missing rate is not free labour. When this is not empty every cost,
   * variance and margin on the row is better than the truth, and the screen
   * says so rather than showing a confident number.
   */
  unratedKinds: string[];
  marginThreshold: number | null;
  marginTone: 'good' | 'watch' | 'warn' | 'danger' | 'loss' | 'unknown' | null;
  plannedMarginPct: number | null;
  marginGapPct: number | null;
  physicalProgress: number; budgetUtilization: number | null;
  /** צפי להשלמה — אותו חישוב בשרת כמו בדרילדאון (forecast-to-complete.ts). */
  forecast: CostForecast;
  /** חלק יחסי מייצור משותף — null כשאינו במיזוג פעיל. העלויות כבר מחולקות. */
  mergeShare?: MergeShare | null;
};

/**
 * פרויקטים מאוחדים (29/09/2026): עלויות צד הייצור של הראשי מתחלקות לפי יחידות.
 * `delta` — השינוי בעלות בפועל (שלילי בראשי, חיובי במצטרף). merge-cost-share.ts.
 */
export type MergeShare = {
  role: 'MAIN' | 'JOIN';
  label: string;
  partners: string[];
  units: number; totalUnits: number;
  pool: number; amount: number; delta: number;
};

/** שורת צפי: יתרת התכנון = max(מתוכנן − בפועל, 0); בפועל + צפי = בפועל + יתרה. */
export type ForecastLine = {
  key: string; label: string; planned: number; actual: number; remaining: number; eac: number; over: number; unplanned: boolean;
};
export type CostForecast = {
  lines: ForecastLine[];
  planned: number; actual: number; remaining: number; eac: number;
  overrun: number; overrunPct: number | null;
  /** מחיר ההזמנה — null בלי הרשאת כספים או כשלא הוזן. */
  price: number | null;
  plannedProfitPct: number | null; eacProfitPct: number | null;
  noPlan: boolean;
};
export type FinanceTotals = { plannedCost: number; actualCost: number; variance: number; salePrice: number; profit: number; projects: number };
export type FinanceBoard = { projects: FinanceRow[]; totals: FinanceTotals };

/** מאיפה הגיע המספר המתוכנן — אומדן ההקמה, גלגול מהפרט, או כלום. */
export type PlannedSource = 'ESTIMATE' | 'ROLLUP' | 'NONE';

export type FinanceDrilldown = {
  number: string; name: string | null;
  /** חלק יחסי מייצור משותף — אותו כלל כמו בלוח. */
  mergeShare?: MergeShare | null;
  /** ההצעה שממנה הפרויקט נפתח — הקישור אחורה, ומה נאמר שזה יעלה. */
  quote: { id: string; seq: number; title: string; estimatedCost: number | null; offeredPrice: number | null } | null;
  engRate: number; installRate: number;
  purchase: {
    planned: number; actual: number; missingCount: number;
    byItem: { sku: string | null; name: string; qty: number; unitPrice: number | null; priceDate: string | null; lineCost: number | null; disciplineLabel?: string }[];
    byDiscipline?: Record<'MECHANICAL' | 'ELECTRICAL' | 'CONTROL' | 'GENERAL', number>;
    plannedByDiscipline?: Record<'MECHANICAL' | 'ELECTRICAL' | 'CONTROL' | 'GENERAL', number>;
    awaitingIssueIls?: number; awaitingIssueLines?: number;
    /** false = הבפועל הוא סכום שהוזן ידנית, לא מהשורות שנופקו. */
    fromPurchasing?: boolean;
  };
  rawMaterial: {
    actual: number; kg: number; missingCount: number;
    byMaterial: { material: string; thicknessMm: number | null; kg: number; cost: number; sheets: number; partlyUnpriced: boolean }[];
  };
  engineering: {
    rate: number; actual: number; plannedHours: number; planned: number; plannedSource: PlannedSource;
    byEngineer: { name: string; hours: number; rate: number; cost: number }[];
    byPhase: { phase: string; hours: number; cost: number }[];
    byDiscipline?: { discipline: string; hours: number; cost: number }[];
  };
  production: {
    actual: number; plannedHours: number; planned: number; plannedSource: PlannedSource;
    byDepartment: { name: string; hours: number; rate: number; cost: number }[];
    /** דקות שדווחו ואין להן תעריף — נספרות בשעות ולא בעלות. אופציונלי: בפריסה
     *  הלקוח חדש מהשרת לכמה שניות. */
    unratedMinutes?: number;
  };
  installation: {
    rate: number; actual: number; plannedHours: number; planned: number; plannedSource: PlannedSource;
    byEntry: { date: string; name: string; hours: number; rate: number; cost: number }[];
  };
  plannedCost: number;
  actualCost: number;
  salePrice: number | null;
  /** אופציונלי: בפריסה הלקוח חדש מהשרת לכמה שניות. */
  forecast?: CostForecast;
  subcontract?: SubcontractCost;
};

/**
 * קבלני משנה לפי דיסציפלינה (משימה 14, אלי 25/09/2026) — אותו מבנה שהשרת
 * מחזיר מ-`subcontract-cost.ts`, בלוח ובדרילדאון.
 */
export type SubcontractRow = {
  discipline: 'MECHANICAL' | 'ELECTRICAL' | 'CONTROL' | 'GENERAL';
  label: string;
  name: string | null;
  /** המחיר המוסכם כפי שהוזן — null = לא הוזן. */
  cost: number | null;
  planned: number;
  actual: number;
  /** REQUISITIONS — מדרישות שנופקו; AGREED — אין עדיין דרישה, המחיר המוסכם נחשב. */
  actualSource: 'REQUISITIONS' | 'AGREED' | 'NONE';
  over: number;
  missingCount: number;
};
export type SubcontractCost = {
  /** סך המתוכנן — השם הישן, נשמר. */
  cost: number;
  planned: number;
  actual: number;
  fromRequisitions: number;
  missingCount: number;
  byDiscipline: SubcontractRow[];
};

export type NcSlaRow = { type: string; responded: number; open: number; total: number; avgResponseHours: number | null };
export type NcSla = { byType: NcSlaRow[]; avgResponseHours: number | null };
export type TrendPoint = { date: string; plannedCost: number; actualCost: number; variance: number; salePrice: number; profit: number; projectCount: number };

export const NC_TYPE_LABEL: Record<string, string> = {
  MATERIAL_SHORTAGE: 'חוסר חומר', PRODUCTION_FAULT: 'אי-התאמת ייצור', PRIOR_STATION_FAULT: 'אי-התאמה בתחנה קודמת',
};

/**
 * ״כמה הפרויקט הזה היה עולה היום?״ — the same quantities at today's prices.
 *
 * `recorded` is what the project actually cost, each hour and each part at the
 * price it was reported at. `today` is that identical work re-priced now. The
 * difference between the two IS the price movement, which is what a quote for a
 * similar job has to start from.
 *
 * `missing` is not decoration: a part with no current catalogue price is left
 * OUT of `today` rather than carried over or zeroed, so a comparison with holes
 * says so instead of looking complete.
 */
export type FinanceReprice = {
  engineering: { recorded: number; today: number };
  production: { recorded: number; today: number };
  installation: { recorded: number; today: number };
  purchase: { recorded: number; today: number };
  rawMaterial: { recorded: number; today: number };
  vehicle: { recorded: number; today: number };
  machine: { recorded: number; today: number };
  support: { recorded: number; today: number };
  total: { recorded: number; today: number };
  missing: { what: string; detail: string }[];
};

/**
 * היעד החודשי ומה שצפוי לצאת מולו.
 *
 * Eli, 2026-09-02: "זה המספר החשוב ביותר במכירות." `expected` is what is DUE in
 * the month by its live delivery date; `actual` is what actually went out. They
 * are different populations on purpose — the gap between them IS the miss, and
 * one combined figure hides it.
 */
export type SalesMonth = {
  month: string; label: string;
  expectedIls: number; expectedCount: number;
  actualIls: number; actualCount: number;
  openToBillIls: number;
  /** מתוך `expectedCount`, כמה פרויקטים אין להם מחיר — `expectedIls` חסר אותם. */
  unpricedCount: number;
  when: 'past' | 'current' | 'future';
};
export type SalesMonthDetail = {
  month: string;
  label: string;
  invoicedIls: number;
  expectedIls: number;
  invoices: Array<{
    id: string; projectNumber: string; projectName: string | null;
    customerName: string | null; label: string; invoiceRef: string | null;
    amountIls: number; invoicedAt: string | null;
  }>;
  expected: Array<{
    kind: 'PROJECT' | 'SERVICE';
    id: string; number: string | null; name: string | null;
    customerName: string | null; amountIls: number | null; date: string | null;
    delivered: boolean; billedIls: number;
  }>;
};

export type SalesPlan = {
  targetIls: number;
  months: SalesMonth[];
  current: SalesMonth;
  currentItems: Array<{
    kind: 'PROJECT' | 'SERVICE'; id: string; number: string | null; name: string | null;
    customerName: string | null; managerName: string | null;
    amountIls: number | null; date: string | null; delivered: boolean;
  }>;
};

/** ההוצאות לפי תת-מכלול, ולפי יחידה אחת מתוך הכמות. */
export type ProjectUnits = {
  number: string; name: string | null; orderedQty: number | null;
  totals: {
    salePrice: number | null; actualCost: number; purchase: number; rawMaterial: number;
    engineering: number; production: number; installation: number; other: number;
    /** ממה מורכב ״אחר״ — רכב, זמן מכונה ועבודות תומכות. */
    /** אופציונלי: במהלך פריסה הלקוח חדש יותר מהשרת לכמה שניות. */
    otherParts?: { vehicle: number; machine: number; support: number };
    /** ק״ג חומר שנחתך, כמה שורות חומר בלי מחיר, וכמה חתכים בלי תמחור עבודה. */
    rawMaterialKg?: number;
    rawMaterialUnpriced?: number;
    machineUnpriced?: number;
    /** כמה מהייצור הוא שעות מפעיל על הלייזר/מסור. */
    productionOnMachines?: number;
    /** נקנה לפרויקט וטרם נופק — כסף שיצא שאינו עדיין עלות. */
    purchaseAwaitingIssue?: number;
    purchaseAwaitingLines?: number;
    /** שורות רכש שנופקו ואין להן מחיר מאף סוג. ₪0 ו"לא תומחר" אינם אותו דבר. */
    purchaseUnpriced?: number;
    /** האם חלק מסכום הרכש נשען על הערכת מהנדס ולא על מחיר שסוכם או קפוא. */
    purchaseEstimated?: boolean;
  };
  perUnit: {
    salePrice: number | null; actualCost: number;
    /** התקציב ליחידה. null = לא הוגדר תקציב, ואז אין מול מה למדוד. */
    plannedCost: number | null;
    /** מחיר פחות מה שנוצל עד עכשיו — חלקי כל עוד הפרויקט רץ. */
    profit: number | null; profitPct: number | null;
    /** מחיר פחות התקציב — הרווח שהפרויקט אמור להסתיים בו. */
    expectedProfit: number | null; expectedProfitPct: number | null;
  };
  delivered: boolean;
  rows: Array<{
    key: string | null; label: string; purchaseIls: number;
    productionHours: number; productionIls: number; totalIls: number; partCount: number;
  }>;
  /** מה לא ניתן לייחס לתת-מכלול, ולמה. Never spread pro-rata. */
  unattributedNote: string;
};

/** פרוסה אחת של עמידה בזמנים — חודש, מנהל או לקוח. */
export type OnTimeSlice = {
  key: string; label: string;
  delivered: number; onTime: number; late: number;
  onTimePct: number | null;
  /** ממוצע ימי איחור, על המאחרים בלבד. null = אף אחד לא איחר. */
  avgDaysLate: number | null;
  worstDaysLate: number | null;
};
export type OnTimeDelivery = {
  measured: number;
  /** הספקות שלא נמדדו כי לא הובטח להן תאריך — לא הצלחות. */
  skippedNoCommitment: number;
  overall: { delivered: number; onTime: number; onTimePct: number | null; avgDaysLate: number | null };
  byMonth: OnTimeSlice[];
  byManager: OnTimeSlice[];
  byCustomer: OnTimeSlice[];
};

export type ManagerMargin = {
  key: string; label: string;
  /** פרויקטים שהסתיימו — ורק הם מרכיבים כל מספר כאן. */
  finished: number;
  salePriceIls: number; actualCostIls: number; profitIls: number;
  /** סך הרווח חלקי סך המכירות — ולא ממוצע האחוזים. */
  weightedPct: number | null;
  simpleAvgPct: number | null;
  /** פרויקטים שעדיין רצים — נספרים, ולא נמדדים. */
  running: number;
  /** הסתיימו ואין להם מחיר, ולכן אין מה למדוד. */
  unpriced: number;
};
export type ManagerMarginReport = { rows: ManagerMargin[]; note: string; runningTotal: number };

export type CdrRow = {
  key: string; label: string; reviews: number; onTime: number; late: number;
  onTimePct: number | null; avgDaysLate: number | null;
  /** סקרים שהתאריך שלהם עבר וטרם נערכו. */
  overdueOpen: number;
};
export type CdrDashboard = {
  byDevEngineer: CdrRow[]; byProdEngineer: CdrRow[];
  measured: number; skippedNoPlan: number;
};

export const financeApi = {
  /** עמידה בזמני אספקה מול התאריך שהובטח ללקוח — לפי חודש, מנהל ולקוח. */
  onTimeDelivery: () => api<OnTimeDelivery>('/api/finance/dash/delivery-on-time'),
  /** ממוצע רווחיות לפי מנהל פרויקט. */
  marginByManager: () =>
    api<ManagerMarginReport>('/api/finance/dash/margin-by-manager'),
  /** עמידה בזמני CDR לפי מהנדס — פיתוח וייצור. */
  cdrByEngineer: () => api<CdrDashboard>('/api/finance/dash/cdr-by-engineer'),
  drilldown: (id: string) => api<FinanceDrilldown>(`/api/finance/projects/${id}/drilldown`),
  /** היעד החודשי, הצפי והבפועל. */
  salesPlan: () => api<SalesPlan>('/api/finance/sales-plan'),
  /** מה בדיוק מרכיב חודש — החשבוניות שנסגרו עליו, ומה שהיה אמור לצאת בו. */
  salesMonth: (month: string) =>
    api<SalesMonthDetail>(`/api/finance/sales-plan/${encodeURIComponent(month)}`),
  /** פירוט ההוצאות של פרויקט לפי תת-מכלול ולפי יחידה. */
  projectUnits: (projectId: string) =>
    api<ProjectUnits>(`/api/finance/project-units/${encodeURIComponent(projectId)}`),
  /** אותה עבודה, במחירי היום — לצד מה שנרשם בפועל. */
  reprice: (id: string) => api<FinanceReprice>(`/api/finance/projects/${id}/reprice`),
  // THE cost board. There is one, and this is it.
  //
  // `/api/finance/board` used to be a second way to the same engine, behind a
  // role check that never consulted the permissions matrix — so the screens
  // calling it showed sale prices to people the matrix says may not see them.
  // Deleted rather than guarded: two doors onto one room is the bug.
  //
  // Sale price and profit are stripped by the SERVER for anyone without
  // VIEW_FINANCE, so a null here means "not permitted" exactly as it means "not
  // filled in" — the screen shows neither, which is right for both.
  board: (scope: 'mine' | 'all' = 'all') =>
    api<FinanceBoard>(`/api/finance/pm-board?scope=${scope}`),
  boardDrilldown: (id: string) => api<FinanceDrilldown>(`/api/finance/pm-board/projects/${id}/drilldown`),
  ncSla: () => api<NcSla>('/api/finance/nc-sla'),
  trends: () => api<TrendPoint[]>('/api/finance/trends'),
  rates: () => api<FinanceRates>('/api/finance/rates'),
  setRates: (body: {
    departmentRates?: Record<string, number>; installationHourlyRate?: number; vehicleKmRate?: number;
    serviceCallOutIls?: number; serviceHourlyPriceIls?: number; servicePartsMarkupPct?: number;
    targetMarginServicePct?: number; targetMarginShortJobPct?: number;
    installNightMealIls?: number; installNightAllowanceIls?: number;
    installNightHotelIls?: number; installNightHotelPerWorkers?: number;
    installNightHoursPerDay?: number;
    fxEurIls?: number; fxUsdIls?: number;
  }) =>
    api<{ ok: true }>('/api/finance/rates', { method: 'PATCH', body }),
  projects: () => api<FinanceProjectInput[]>('/api/finance/projects'),
  setProjectInputs: (id: string, body: Partial<{ purchasePlanned: number | null; purchaseActual: number | null; salePrice: number | null; installPlannedHours: number | null }>) =>
    api<{ ok: true }>(`/api/finance/projects/${id}/inputs`, { method: 'PATCH', body }),
};

// ── מסך המנכ״ל והדשבורדים ────────────────────────────────────────────────────

export type CeoRow = {
  number: string; name: string | null; customerName: string | null;
  /** `planned: null` = לא נקבע תקציב. אפס הוא תשובה אחרת לגמרי. */
  engineering: { planned: number | null; actual: number };
  production: { planned: number | null; actual: number };
  purchase: { planned: number | null; actual: number };
  installation: { planned: number | null; actual: number };
  release: { planned: string | null; actual: string | null; lateDays: number | null };
  delivery: { planned: string | null; actual: string | null; lateDays: number | null };
  /** רווח עד כה — מחיר פחות מה שהוצא. גבוה בפרויקט צעיר, מטבעו. */
  profitPct: number | null; profit: number | null;
  /**
   * הרווח הצפוי בסיום — מחיר פחות מה שהפרויקט צפוי לעלות.
   *
   * זה המספר שהצבע והמיון קוראים, ולכן זה מה שהמסך מציג. null = אין תוכנית,
   * ואז אי אפשר לומר מה צפוי.
   */
  expectedProfit: number | null; expectedProfitPct: number | null;
  /** רווח רצוי — מחיר פחות העלות המתוכננת. ההבטחה שעליה נמכרה העבודה. */
  desiredProfit: number | null; desiredProfitPct: number | null;
  plannedCost: number; actualCost: number;
  salePrice: number | null;
  /** „באיחור" — ההגדרה האחת: הצפי אחרי ההבטחה ללקוח (גם לפני שהגיעה). */
  late: boolean; deliveryForecast: string | null; overrunDays: number | null;
  /** רווח לפי התקדמות: האחוז של כרטיס הפרויקט (100 = הסתיים; null = אין מדד → 0%), מחיר × אחוז, פחות העלות. */
  progressPct: number | null; earned: number | null; earnedProfit: number | null;
  /** מאיפה האחוז: פעולות ייצור, אבני דרך (כשאין פעולות), או הסתיים. */
  progressSource: 'STEPS' | 'MILESTONES' | 'DONE' | null;
  health: 'GOOD' | 'WATCH' | 'BAD';
};

export type CeoBoard = {
  rows: CeoRow[];
  totals: {
    projects: number; plannedCost: number; actualCost: number;
    salePrice: number; profit: number; profitPct: number | null;
    /** רווח רצוי — בשקלים ובאחוזים. */
    desiredProfit: number; desiredProfitPct: number | null;
    /** רווח בפועל — בשקלים ובאחוזים. */
    actualProfit: number; actualProfitPct: number | null;
    /** מחיר × אחוז התקדמות, על המתומחרים; וכמה מהם בלי מדד (נספרו 0%). */
    earnedValue: number; noProgressPriced: number;
    unpricedProjects: number;
    /**
     * כמה פרויקטים נושאים מחיר, וכמה מהם באמת נגמרו.
     *
     * „רווח בפועל“ הוא מחיר פחות מה שהוצא עד כה, ולכן על עבודה שעדיין
     * רצה הוא אומדן. בלי שני המספרים האלה הכותרת נקראת כתוצאה שהושגה.
     */
    pricedProjects: number;
    deliveredPricedProjects: number;
    late: number; losing: number;
  };
};

export type InvoiceMonth = {
  month: string;
  invoiced: number; invoicedCount: number;
  due: number; dueCount: number;
  forecast: number; forecastCount: number;
  target: number; attainmentPct: number;
};

export type InvoiceForecast = {
  months: InvoiceMonth[];
  current: Array<{
    projectNumber: string; projectName: string | null; customerName: string | null;
    label: string; amount: number; status: string;
    dueAt: string | null; invoicedAt: string | null; expectedAt: string | null;
  }>;
  hasDeliveryNotes: false;
};

export type OnTimeRow = {
  key: 'RELEASE' | 'PDR' | 'CDR' | 'FAT' | 'DELIVERY';
  label: string; onTime: number; late: number; measured: number;
  onTimePct: number | null; avgLateDays: number | null;
  worst: Array<{ number: string; name: string | null; lateDays: number }>;
};

export type NcResponseRow = {
  category: string; closed: number;
  avgHours: number | null; medianHours: number | null;
  open: number; oldestOpenDays: number | null;
};

export type MachineMonthRow = { machineId: string; code: string; name: string; hours: number; scans: number; units: number };

export type WorkerPartRow = { partNumber: string; projectNumber: string; hours: number; units: number; scans: number };
export type WorkerRow = {
  employeeId: string; name: string; departmentName: string | null;
  hours: number; units: number; scans: number; parts: WorkerPartRow[];
};

export const ceoApi = {
  board: () => api<CeoBoard>('/api/finance/ceo-board'),
  invoices: () => api<InvoiceForecast>('/api/finance/invoice-forecast'),
  onTime: () => api<{ rows: OnTimeRow[] }>('/api/finance/dash/on-time'),
  ncResponse: () => api<{ rows: NcResponseRow[]; totalOpen: number }>('/api/finance/dash/nc-response'),
  machineHours: (month?: string) =>
    api<{ month: string; rows: MachineMonthRow[]; totalHours: number; unassignedHours: number }>(
      `/api/finance/dash/machine-hours${month ? `?month=${month}` : ''}`),
  workers: (month?: string) =>
    api<{ month: string; rows: WorkerRow[]; top5: WorkerRow[]; totalHours: number; totalUnits: number }>(
      `/api/finance/dash/worker-reporting${month ? `?month=${month}` : ''}`),
};

export const shekel = (n: number | null | undefined): string =>
  n == null ? '—' : `₪${Math.round(n).toLocaleString('he-IL')}`;
